(() => {
  'use strict';
  if (customElements.get('pawly-bundle-preview')) return;
  class PawlyBundlePreview extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.rootUrl = window.Shopify?.routes?.root || '/';
      this.featuredProduct = JSON.parse(this.querySelector('[data-bed-product]').textContent);
      this.productRoot = this.closest('xo-product');
      this.form = document.getElementById(this.dataset.formId);
      this.threshold = Number(this.dataset.threshold) || 0;
      this.summary = this.querySelector('[data-cart-summary]');
      this.estimate = this.querySelector('[data-bed-estimate]');
      this.status = this.querySelector('[data-bundle-status]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const add = event.target.closest('[data-add-companion]');
        const refresh = event.target.closest('[data-refresh]');
        if (!add && !refresh) return;
        event.preventDefault(); event.stopPropagation();
        if (add) this.addCompanion(add); else this.refreshCart();
      }, options);
      if (typeof window.cartSubscribe === 'function') this.unsubscribeCart = window.cartSubscribe(state => { if (!state.addIdLoading) this.refreshCart(); });
      if (typeof window.cartFormSubscribe === 'function') this.unsubscribeQuantity = window.cartFormSubscribe(() => setTimeout(() => this.render(), 0));
      this.productRoot?.addEventListener('change', () => setTimeout(() => this.render(), 50), options);
      this.productRoot?.addEventListener('input', () => this.render(), options);
      this.addEventListener('focusin', () => {
        if (Date.now() - (this.checkedAt || 0) > 3000) this.refreshCart();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.refreshCart();
      }, options);
      this.cartObserver = new MutationObserver(() => this.refreshCart());
      document.querySelectorAll('xo-cart-size').forEach(node => this.cartObserver.observe(node, { childList: true, characterData: true, subtree: true }));
      this.refreshCart();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.cartObserver?.disconnect();
      this.unsubscribeCart?.();
      this.unsubscribeQuantity?.();
      this.started = false;
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.cart?.currency || this.dataset.currency || 'USD' }).format(cents / 100);
    }
    async refreshCart() {
      if (this.refreshing) { this.refreshAgain = true; return; }
      this.refreshing = true;
      try {
        const response = await fetch(this.rootUrl + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        this.cart = await response.json();
        this.checkedAt = Date.now();
        this.render();
      } catch (_) {
        this.summary.textContent = 'Cart total could not be refreshed. View your cart to check the total.';
        this.estimate.textContent = '';
      } finally {
        this.refreshing = false;
        if (this.refreshAgain) { this.refreshAgain = false; this.refreshCart(); }
      }
    }
    render() {
      if (!this.cart) return;
      const subtotal = Number(this.cart.total_price) || 0;
      const eligibleCurrency = this.cart.currency === 'USD';
      const gap = Math.max(0, this.threshold - subtotal);
      this.summary.textContent = `In your cart: ${this.money(subtotal)}.`;
      if (this.threshold && eligibleCurrency) {
        this.summary.textContent += gap > 0
          ? ` Add ${this.money(gap)} to reach the US$${(this.threshold / 100).toFixed(2)} free-shipping merchandise threshold.`
          : ' Merchandise threshold reached; eligible shipping is confirmed at checkout.';
      } else if (!eligibleCurrency) {
        this.summary.textContent += ' Shipping eligibility is confirmed at checkout for your currency and address.';
      }
      const variantId = this.form?.querySelector('input[name="id"]')?.value;
      const variant = this.featuredProduct.variants.find(item => String(item.id) === variantId);
      const quantity = Math.max(1, Number(this.form?.querySelector('[name="quantity"]')?.value) || 1);
      this.estimate.textContent = variant && variant.available
        ? `After adding this product (${quantity}), estimated cart: ${this.money(subtotal + variant.price * quantity)}${this.threshold && eligibleCurrency ? '; estimated gap: ' + this.money(Math.max(0, this.threshold - subtotal - variant.price * quantity)) : ''}. Not yet added; discounts may change this estimate.`
        : 'Selected product option is unavailable. Choose an available option above.';
      this.querySelectorAll('[data-companion]').forEach(card => {
        const option = card.querySelector('select').selectedOptions[0];
        const price = Number(option?.dataset.price) || 0;
        card.querySelector('[data-companion-price]').textContent = this.money(price) + ' ' + this.cart.currency;
        card.querySelector('[data-gap-fit]').textContent = this.threshold && eligibleCurrency && gap > 0 && price >= gap ? 'This option covers the current cart gap.' : '';
      });
    }
    async addCompanion(button) {
      if (this.adding) return;
      const variantId = button.closest('[data-companion]').querySelector('select').value;
      if (!variantId) return;
      this.adding = true;
      this.querySelectorAll('[data-add-companion]').forEach(item => { item.disabled = true; });
      this.status.textContent = 'Adding your selected extra…';
      try {
        const sectionIds = [...document.querySelectorAll('xo-cart-mini[id],xo-cart[id]')].map(item => item.id).filter(Boolean).slice(0, 5);
        const response = await fetch(this.rootUrl + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds, sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok) { const rejection = new Error(result.description || 'This option could not be added.'); rejection.confirmedRejection = true; throw rejection; }
        const cartResponse = await fetch(this.rootUrl + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart confirmation unavailable');
        this.cart = await cartResponse.json();
        this.checkedAt = Date.now();
        const store = window['xo-webcomponents']?.xoStore;
        if (store && result.sections) {
          store.set('xo-cart', state => ({ ...state, size: this.cart.item_count, sections: result.sections, addIdLoading: '', addErrorMessage: '', isAdded: true, variantId: variantId }))('pawly-bundle/addSuccess');
        } else {
          window.location.reload();
          return;
        }
        this.status.textContent = 'Added 1 extra to your cart. Use “View cart” for combined checkout.';
        this.render();
      } catch (error) {
        this.status.textContent = error.confirmedRejection ? error.message : 'Could not confirm the addition. Check your cart before trying again to avoid adding it twice.';
        await this.refreshCart();
      } finally {
        this.adding = false;
        this.querySelectorAll('[data-add-companion]').forEach(item => { item.disabled = false; });
      }
    }
  }
  customElements.define('pawly-bundle-preview', PawlyBundlePreview);
})();
